import type { Tenant } from '@vehix/portal/services/api/tenants';
import { getDomainFromWebsite, getEmailDomain, isFreeEmailDomain } from './emailIdentity';

/**
 * Infers which tenant a new user belongs to from their email domain.
 *
 * There is no domain column on prod.tenants, so rather than add one we read
 * the signal that already exists: the tenant assignments of users who share
 * the domain. The admin page has both lists in memory already (the approvals
 * list and the tenant list), so this costs zero extra queries.
 *
 * Against live data 14 of 17 domains resolve to exactly one tenant. The three
 * that don't are the cases this must NOT guess at: kebi.ai (internal staff,
 * spread across 5 tenants), lithia.com (a dealer group with 4), and gmail.com
 * (a consumer provider). Those get ranked suggestions instead of an auto-fill.
 *
 * This is a SUGGESTION, never a decision — the tenant dropdown always stays
 * editable and a manual choice is never overwritten.
 */

/**
 * Minimal shape needed from an approval row.
 * `| undefined` is explicit so this stays assignable from callers compiled
 * with exactOptionalPropertyTypes.
 */
export interface DomainIndexApproval {
  email: string;
  tenant_id?: string | null | undefined;
}

export interface TenantSuggestion {
  /** Candidate tenant ids, most-users-first. Never empty. */
  tenantIds: string[];
  /**
   * True only when a single tenant owns the domain, i.e. safe to auto-select.
   * False means "show these first but let the admin pick".
   */
  confident: boolean;
  /** Where the match came from — drives the wording of the UI hint. */
  source: 'existing-users' | 'tenant-profile';
  /** How many existing users back an 'existing-users' match. */
  userCount: number;
}

/** domain -> suggestion. Build once per approvals/tenants change via useMemo. */
export type DomainTenantIndex = Map<string, TenantSuggestion>;

/**
 * Pass 1 — tally how many existing users each tenant has per email domain.
 * This is the strongest signal: it reflects how the domain has actually been
 * assigned in practice rather than how someone configured it.
 */
function indexFromExistingUsers(
  approvals: DomainIndexApproval[],
  validTenantIds: Set<string>
): DomainTenantIndex {
  const domainCounts = new Map<string, Map<string, number>>();

  for (const approval of approvals) {
    if (!approval.tenant_id || !validTenantIds.has(approval.tenant_id)) continue;

    const domain = getEmailDomain(approval.email);
    if (!domain || isFreeEmailDomain(domain)) continue;

    let perTenant = domainCounts.get(domain);
    if (!perTenant) {
      perTenant = new Map<string, number>();
      domainCounts.set(domain, perTenant);
    }
    perTenant.set(approval.tenant_id, (perTenant.get(approval.tenant_id) || 0) + 1);
  }

  const index: DomainTenantIndex = new Map();
  for (const [domain, perTenant] of domainCounts) {
    const ranked = [...perTenant.entries()].sort((a, b) => b[1] - a[1]);
    index.set(domain, {
      tenantIds: ranked.map(([tenantId]) => tenantId),
      confident: ranked.length === 1,
      source: 'existing-users',
      userCount: ranked.reduce((sum, [, count]) => sum + count, 0)
    });
  }
  return index;
}

/**
 * Pass 2 — fall back to the tenant's own `website` / `primary_email` domain.
 * Only fills domains no existing user has already claimed, so a brand-new
 * tenant with no users yet can still be matched.
 */
function addTenantProfileDomains(index: DomainTenantIndex, tenants: Tenant[]): void {
  for (const tenant of tenants) {
    const candidates = [
      getDomainFromWebsite(tenant.website),
      tenant.primary_email ? getEmailDomain(tenant.primary_email) : undefined
    ];

    for (const domain of candidates) {
      if (!domain || isFreeEmailDomain(domain) || index.has(domain)) continue;
      index.set(domain, {
        tenantIds: [tenant.id],
        confident: true,
        source: 'tenant-profile',
        userCount: 0
      });
    }
  }
}

/** Builds the domain -> tenant index from existing users, then tenant profiles. */
export function buildDomainTenantIndex(
  approvals: DomainIndexApproval[],
  tenants: Tenant[] | undefined
): DomainTenantIndex {
  const tenantList = tenants || [];
  const validTenantIds = new Set(tenantList.map(t => t.id));
  const index = indexFromExistingUsers(approvals, validTenantIds);
  addTenantProfileDomains(index, tenantList);
  return index;
}

/**
 * Looks up a suggestion for an email address.
 * Returns undefined for unusable addresses, consumer domains, and unknown
 * domains — all cases where staying silent beats guessing.
 */
export function suggestTenantForEmail(
  email: string,
  index: DomainTenantIndex
): TenantSuggestion | undefined {
  const domain = getEmailDomain(email);
  if (!domain || isFreeEmailDomain(domain)) return undefined;
  return index.get(domain);
}

/**
 * Orders tenants for the dropdown so suggested ones sit at the top in
 * suggestion rank order, with the rest alphabetical beneath them.
 */
export function sortTenantsBySuggestion(
  tenants: Tenant[],
  suggestion: TenantSuggestion | undefined
): Tenant[] {
  const nameOf = (t: Tenant) => (t.name || t.tenant_name || '').toLowerCase();

  if (!suggestion || suggestion.tenantIds.length === 0) {
    return [...tenants].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }

  const rank = new Map(suggestion.tenantIds.map((id, i) => [id, i]));
  return [...tenants].sort((a, b) => {
    const rankA = rank.get(a.id);
    const rankB = rank.get(b.id);
    if (rankA !== undefined && rankB !== undefined) return rankA - rankB;
    if (rankA !== undefined) return -1;
    if (rankB !== undefined) return 1;
    return nameOf(a).localeCompare(nameOf(b));
  });
}
